import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { DateDisplay } from "../../../components";
import { FormInput } from "../../../components/forms";
import { HeadOfficeMasterAccountDesignationsSelect, HeadOfficeMasterAccountSelect, HeadOfficeWrapperProductsSelect, HeadOfficeWrappersSelect } from "../../../components/forms/selects";
import { useCustodianTransactionMatching } from "../hooks/CustodianTransactionMatchingContext";
import MissingAccountsGridRowActionCol from "./MissingAccountsGridRowActionCol";
import {openConfirmModal} from "../../../components/modals/ConfirmModal";

const MissingAccountsGridRow = ({ data }) => {

    const { potentialAccountMatches: { createPlatformAccountDesignationMapping, blacklistPlatformAccount } } = useCustodianTransactionMatching();
    const [isEditable, setIsEditable] = useState(false);
    const [platformAccountMapping, setPlatformAccountMapping] = useState(data);
    
    useEffect(() => setPlatformAccountMapping(data), [data]);

    const {
        custodianCode,
        dateAdded,
        designation,
        designationId,
        masterAccountId,
        masterAccountReference,
        masterAccountName,
        platformAccountId,
        platformAccountName,
        productId,
        productName,
        organisationId,
        score,
        wrapperId,
        wrapperName,
        providerReference
    } = platformAccountMapping;

    const isValidMapping = useMemo(() => {
        return !designationId || !platformAccountId || !productId
    }, [designationId, platformAccountId, productId]);

    const handleConfirm = () => {
        const request = {
            organisationId,
            custodianCode,
            designationId,
            platformAccountId,
            productId,
            providerReference
        };

        toast.promise(createPlatformAccountDesignationMapping(request), {
            loading: "Creating mapping...",
            error: (err) => {
                return err?.data ?? "Unable to create mapping."
            },
            success: (data) => {
                return "Mapping created!";
            }
        });
    };

    const handleBlacklistAccount = async (account) => {
        const blacklist = await openConfirmModal({
            dialogTitle: `Blacklist ${account.platformAccountId} - ${account.platformAccountName}`,
            message: "Are you sure you want to blacklist this account?",
            confirmButtonText: "Yes",
        });

        const request = {
            platformAccountId : account.platformAccountId
        };

        if (!blacklist) {
            return;
        }

        toast.promise(blacklistPlatformAccount(request), {
            loading: "Creating blacklist...",
            error: (err) => {
                return err?.data ?? "Unable to create blacklist."
            },
            success: (data) => {
                return "Blacklist created!";
            }
        });
    };

    const updateMapping = (keyValues) => {
        // so we can pass in { masterAccountId, masterAccountName } etc and assign both
        setPlatformAccountMapping(prev => ({
            ...prev,
            ...keyValues
        }));
    };

    useEffect(() => {
        if(wrapperId === null) updateMapping({ productId: null, productName: null });
    }, [wrapperId]);

    if (isEditable) {
        return (
            <tr onDoubleClick={() => setIsEditable(false)} key={platformAccountId}>
                <td className="col-2">
                    <HeadOfficeMasterAccountSelect
                        valueObject={{
                            value: masterAccountId,
                            label: masterAccountName
                        }}
                        onChange={({ label, value, data: { accountRef } }) => updateMapping({
                            masterAccountId: value,
                            masterAccountName: label,
                            masterAccountReference: accountRef
                        })}
                    />
                </td>
                <td className="col-2">
                    <HeadOfficeMasterAccountDesignationsSelect
                        defaultValue={designationId}
                        masterAccountId={masterAccountId}
                        onChange={({ value, label }) => updateMapping({
                            designationId: value,
                            designation: label
                        })}
                    />
                </td>
                <td>{custodianCode}</td>
                <td className="col-2">
                    {platformAccountId} - {platformAccountName}
                </td>
                <td className="col-2">
                    <HeadOfficeWrappersSelect
                        defaultValue={wrapperId}
                        onChange={({ value, label }) => updateMapping({ wrapperId: value, wrapperName: label })}
                    />
                </td>
                <td className="col-2">
                    <HeadOfficeWrapperProductsSelect
                        defaultValue={productId}
                        wrapperId={wrapperId}
                        onChange={({ value, label }) => updateMapping({ productId: value, productName: label })}
                    />
                </td>
                <td className="col-2">
                    <FormInput
                        value={providerReference}
                        onChange={(e) => updateMapping({providerReference: e.target.value})}
                    />
                </td>
                <td>
                    {
                        moment(dateAdded).isValid()
                            ? <DateDisplay>{dateAdded}</DateDisplay>
                            : <span className="px-2">-</span>
                    }
                </td>
                <td align="right">{score}</td>
                <td><MissingAccountsGridRowActionCol
                    canConfirm={isValidMapping}
                    confirmFn={handleConfirm}
                    setShowEdit={setIsEditable}
                    showBlacklistAccount={() => {
                        handleBlacklistAccount(platformAccountMapping);
                    }}
                />
                </td>
            </tr>
        );
    }


    return (
        <tr onDoubleClick={() => setIsEditable(true)} key={platformAccountId}>
            <td>{masterAccountReference}</td>
            <td>{designation}</td>
            <td>{custodianCode}</td>
            <td>{platformAccountId} - {platformAccountName}</td>
            <td>{wrapperName}</td>
            <td>{productName}</td>
            <td>{providerReference}</td>
            <td>
                {
                    moment(dateAdded).isValid()
                        ? <DateDisplay>{dateAdded}</DateDisplay>
                        : <span className="px-2">-</span>
                }
            </td>
            <td align="right">{score}</td>
            <td><MissingAccountsGridRowActionCol
                canConfirm={isValidMapping}
                confirmFn={handleConfirm}
                setShowEdit={setIsEditable}
                showBlacklistAccount={() => {
                        handleBlacklistAccount(data);
                    }}
            /></td>
        </tr>
    );
};

export default MissingAccountsGridRow;